import type { PermissionCode } from "@cafegestor/shared";
import { Link } from "@tanstack/react-router";
import { useCan, useMe } from "@/app/me";
import { Icon, type IconName } from "@/components/icons";
import { Card, PageTitle } from "@/components/ui";
import { StatusPage } from "./StatusPage";

type ShortcutTone = "caramel" | "blue" | "green" | "purple";

/**
 * Atalhos da página inicial (Fase C): só as quatro tarefas do dia a dia, e só as que a pessoa
 * pode usar. Entradas, saldos e clientes continuam no menu lateral.
 */
const SHORTCUTS: {
  to:
    | "/vendas/venda-direta"
    | "/vendas/pedidos"
    | "/financeiro/contas-a-receber"
    | "/financeiro/contas-a-pagar";
  label: string;
  hint: string;
  icon: IconName;
  tone: ShortcutTone;
  /** todas estas permissões */
  permissions: PermissionCode[];
}[] = [
  {
    to: "/vendas/venda-direta",
    label: "Venda Direta",
    hint: "Realizar vendas rapidamente",
    icon: "vendas",
    tone: "caramel",
    permissions: ["vendas.pedido.criar", "vendas.pedido.confirmar", "fiscal.documento.emitir"],
  },
  {
    to: "/vendas/pedidos",
    label: "Pedidos de venda",
    hint: "Criar e acompanhar pedidos",
    icon: "pedidos",
    tone: "blue",
    permissions: ["vendas.ver"],
  },
  {
    to: "/financeiro/contas-a-receber",
    label: "Contas a Receber",
    hint: "Acompanhar recebimentos",
    icon: "receber",
    tone: "green",
    permissions: ["financeiro.ver"],
  },
  {
    to: "/financeiro/contas-a-pagar",
    label: "Contas a Pagar",
    hint: "Controlar pagamentos e vencimentos",
    icon: "pagar",
    tone: "purple",
    permissions: ["financeiro.ver"],
  },
];

const TONE: Record<ShortcutTone, { card: string; icon: string }> = {
  caramel: {
    card: "border-caramel/40 bg-caramel-soft hover:border-caramel",
    icon: "bg-accent text-accent-foreground",
  },
  blue: { card: "border-info/20 bg-info-soft hover:border-info/50", icon: "bg-card text-info" },
  green: {
    card: "border-success/20 bg-success-soft hover:border-success/50",
    icon: "bg-card text-success",
  },
  purple: {
    card: "border-purple/20 bg-purple-soft hover:border-purple/50",
    icon: "bg-card text-purple",
  },
};

export function HomePage() {
  const { data: me } = useMe();
  const can = useCan();
  const shortcuts = SHORTCUTS.filter((s) => s.permissions.every((p) => can(p)));
  return (
    <div className="space-y-6">
      <PageTitle
        title={`Olá${me?.user.fullName ? `, ${me.user.fullName.split(" ")[0]}` : ""}`}
        description="Gestão da torrefação: cadastros, estoque e produção, compras, vendas, cargas, financeiro e fiscal."
      />
      {shortcuts.length > 0 && (
        <section aria-labelledby="atalhos-titulo" className="@container">
          <h2 id="atalhos-titulo" className="mb-3 text-[17px] font-semibold">
            Atalhos
          </h2>
          {/* pela largura da área (menu aberto ou recolhido): 1 coluna no celular, 2 no tablet, 4 no desktop */}
          <ul className="grid grid-cols-1 gap-4 @lg:grid-cols-2 @4xl:grid-cols-4">
            {shortcuts.map((s) => {
              const tone = TONE[s.tone];
              const featured = s.tone === "caramel";
              return (
                <li key={s.to} className="flex">
                  <Link
                    to={s.to}
                    className={`group flex w-full flex-col gap-4 rounded-xl border shadow-card transition-all hover:-translate-y-0.5 hover:shadow-pop focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring ${featured ? "p-6" : "p-5"} ${tone.card}`}
                  >
                    <span className="flex items-start justify-between gap-3">
                      <span
                        className={`flex items-center justify-center rounded-xl ${featured ? "size-12" : "size-11"} ${tone.icon}`}
                      >
                        <Icon name={s.icon} size={featured ? 24 : 22} />
                      </span>
                      <span className="text-muted-foreground transition-transform group-hover:translate-x-0.5 group-hover:text-foreground">
                        <Icon name="arrowRight" size={20} />
                      </span>
                    </span>
                    <span className="flex flex-col gap-1">
                      <span className={`font-semibold ${featured ? "text-lg" : "text-base"}`}>
                        {s.label}
                      </span>
                      <span className="text-sm text-secondary-foreground">{s.hint}</span>
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}
      {me?.activeCompany && (
        <Card title="Seu acesso nesta empresa">
          <p className="text-sm text-muted-foreground">
            {me.activeCompany.permissions.length} permissões ativas em {me.activeCompany.name}.
          </p>
        </Card>
      )}
      <StatusPage />
    </div>
  );
}
